/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import Component from 'can/component/component';
import FeedService from 'scripts/services/base-feed-service';
import template from './player-course-searchbar.stache';
import urlUtil from 'scripts/utils/url';
import dtm from 'scripts/services/dtm';

const playerCourseAvailableFiltersURL =
    window.globalSettings.feeds.playerCourseAvailableFilters;
const playerCollectionURL = window.globalSettings.pagePaths.playerCollectionURL;
const courseCollectionURL = window.globalSettings.pagePaths.courseCollectionURL;

const encodeItemIdentifier = (decoded) =>
    decoded.replace(/\s/g, '-').toLowerCase();
const mapFeedItemToEntry = (item) => {
    return {
        identifier: item.value,
        entry: item.text,
        hash: encodeItemIdentifier(item.text),
    };
};

const key = {
    tab: 9,
    enter: 13,
    esc: 27,
    up: 38,
    down: 40,
};

Component.extend({
    tag: 'player-course-searchbar',
    template: template,
    viewModel: {
        define: {
            players: {
                type: 'array',
                value: [],
                set: function(playersFeed) {
                    return playersFeed
                        .map(mapFeedItemToEntry)
                        .sort((player1, player2) => {
                            const player1LastName = player1.entry.split(' ')[1];
                            const player2LastName = player2.entry.split(' ')[1];
                            return player1LastName.localeCompare(
                                player2LastName,
                            );
                        });
                },
            },
            courses: {
                type: 'array',
                value: [],
                set: function(coursesFeed) {
                    return coursesFeed
                        .map(mapFeedItemToEntry)
                        .sort((course1, course2) =>
                            course1.entry.localeCompare(course2.entry),
                        );
                },
            },
            matchedPlayers: {
                type: 'array',
                value: [],
            },
            matchedCourses: {
                type: 'array',
                value: [],
            },
            selectedItem: {
                Type: can.Map,
                value: new can.Map({
                    index: -1,
                    identifier: '',
                    entry: '',
                }),
            },
            searchValue: {
                type: 'string',
                value: '',
            },
            isActive: {
                type: 'boolean',
                value: false,
            },
            noResult: {
                type: 'boolean',
                value: false,
            },
            getUniqueId: {
                get() {
                    return this.attr('uniqueModuleId');
                },
            },
            enteredValue: {
                type: 'string',
                value: '',
            },
            textToRead: {
                type: 'string',
                value: '',
            },
        },
        handleSearchValueChange: function(event) {
            const kc = event.keyCode;
            if (
                (this.attr('isActive') && (kc === key.up || kc === key.down)) ||
                kc === key.tab ||
                kc === key.enter ||
                kc === key.esc
            ) {
                return;
            }
            const searchValue = can.trim($(event.target).val());
            this.attr('enteredValue', searchValue);

            if (searchValue) {
                const matchedPlayers = this.doSearch(
                    this.attr('players'),
                    searchValue,
                );
                this.attr('matchedPlayers', matchedPlayers);
                const matchedCourses = this.doSearch(
                    this.attr('courses'),
                    searchValue,
                );
                this.attr('matchedCourses', matchedCourses);
                const noResult =
                    matchedPlayers.length < 1 && matchedCourses.length < 1;
                this.attr('noResult', noResult);
                this.attr('isActive', true);
                const numbers = matchedPlayers.length + matchedCourses.length;
                this.announceResults(numbers);
            } else {
                this.resetSearchValue();
            }
        },
        doSearch: function(searchList, searchValue) {
            const searchQuery = searchValue.toLowerCase();
            return searchList
                .serialize()
                .filter(
                    (item) =>
                        item.entry.toLowerCase().indexOf(searchQuery) > -1,
                );
        },
        resetSearchValue: function() {
            this.attr('matchedPlayers', []);
            this.attr('matchedCourses', []);
            this.attr('noResult', false);
            this.attr('isActive', false);
        },
        doSelect: function(itemToSelect) {
            const indexMatcher = (item) =>
                item.identifier === itemToSelect.identifier;
            const suggestedPlayerIndex = this.attr('matchedPlayers')
                .serialize()
                .findIndex(indexMatcher);
            if (suggestedPlayerIndex > -1) {
                this.attr('selectedItem', {
                    index: suggestedPlayerIndex,
                    identifier: itemToSelect.identifier,
                    entry: itemToSelect.entry,
                    collection: 'players',
                });
            }

            const suggestedCourseIndex = this.attr('matchedCourses')
                .serialize()
                .findIndex(indexMatcher);
            if (suggestedCourseIndex > -1) {
                const itemIndex =
                    suggestedCourseIndex + this.attr('matchedPlayers').length;
                this.attr('selectedItem', {
                    index: itemIndex,
                    identifier: itemToSelect.identifier,
                    entry: itemToSelect.entry,
                    collection: 'courses',
                });
            }
            this.attr('searchValue', itemToSelect.entry);
        },
        selectItem: function(itemToSelect) {
            this.doSelect(itemToSelect);
            this.attr('searchValue', itemToSelect.entry);
            this.resetSearchValue();
        },
        selectInput: function(index) {
            const enteredVal = this.attr('enteredValue');
            if (!enteredVal) {
                return;
            }
            const searchVal = this.attr('searchValue');
            const selectedItem = this.attr('selectedItem').serialize();
            if (
                selectedItem.identifier === 'searchInput' &&
                searchVal === enteredVal
            ) {
                this.scroll(index);
                this.scrollTableWrapper();
            } else {
                this.attr('selectedItem', {
                    index: selectedItem.index,
                    identifier: 'searchInput',
                    entry: selectedItem.entry,
                    collection: selectedItem.collection,
                });
                this.attr('searchValue', enteredVal);
            }
        },
        announceResults: function(number) {
            const directions =
                'Keyboard users, use up and down arrows to review and enter to select.  Touch device users, explore by touch or with swipe gestures.';
            let textToRead = `${number} results are available. ${directions}`;
            if (number === 0) {
                textToRead =
                    'No results found. Try searching for another player or course.';
            }
            this.attr('textToRead', textToRead);
        },
        scrollTableWrapper() {
            const tableWrapper = $('.js-table-wrapper');
            if (tableWrapper.length === 0) {
                return;
            }
            const tableScrollTop = tableWrapper.scrollTop();
            const tableTop = tableWrapper.offset().top;
            const tableBottom = tableTop + tableWrapper.height();
            const selectedItem = tableWrapper.find('.is-selected');
            if (selectedItem.length === 0) {
                return;
            }
            const selectedItemTop = selectedItem.offset().top;
            const selectedItemBottom = selectedItemTop + selectedItem.height();

            if (tableBottom <= selectedItemBottom) {
                tableWrapper.animate(
                    {
                        scrollTop:
                            tableScrollTop + selectedItemBottom - tableBottom,
                    },
                    100,
                );
            } else if (tableTop >= selectedItemTop) {
                tableWrapper.animate(
                    {
                        scrollTop:
                            tableScrollTop - (tableTop - selectedItemTop),
                    },
                    100,
                );
            }
        },
        scroll: function(delta) {
            const selectedItem = this.attr('selectedItem').serialize();
            const newSelectedItemIndex = selectedItem.index + delta;
            const items = this.attr('matchedPlayers')
                .concat(this.attr('matchedCourses'))
                .serialize();

            if (newSelectedItemIndex < 0) {
                this.selectInput(items.length - 1);
                return;
            } else if (newSelectedItemIndex >= items.length) {
                this.selectInput(-items.length + 1);
                return;
            }

            const newSelectedItem = items[newSelectedItemIndex];
            this.doSelect(newSelectedItem);
        },
        selectItemByAttribute(attributeName, attributeValue) {
            const selectedItem = this.getItemByAttribute(
                attributeName,
                attributeValue,
            );
            if (selectedItem) {
                this.selectItem(selectedItem);
                return selectedItem;
            }

            return;
        },
        getItemByAttribute(attributeName, attributeValue) {
            const searchExpr = (item) => item[attributeName] === attributeValue;
            const suggestedPlayer = this.attr('players')
                .serialize()
                .find(searchExpr);
            if (suggestedPlayer) {
                return suggestedPlayer;
            }
            const suggestedCourse = this.attr('courses')
                .serialize()
                .find(searchExpr);
            if (suggestedCourse) {
                return suggestedCourse;
            }
            return null;
        },
    },
    events: {
        inserted() {
            new FeedService({
                feedUrl: playerCourseAvailableFiltersURL,
                fromCache: false,
                autoUpdate: false,
            })
                .on('data', (type, data) => {
                    if (data.players) {
                        this.viewModel.attr('players', data.players);
                    }
                    if (data.courses) {
                        this.viewModel.attr('courses', data.courses);
                    }

                    if (this.isPlayerCourseCollectionsPage()) {
                        const currentHashItem = this.getCurrentHashItem();
                        if (currentHashItem) {
                            this.viewModel.selectItemByAttribute(
                                'hash',
                                currentHashItem.identifier,
                            );
                        }
                    }
                })
                .on('error', (error) => {
                    console.error('Error fetching player-course data', error);
                })
                .fetch();

            this.registerRouteListener();
        },

        isPlayerCourseCollectionsPage() {
            return (
                window.location.pathname === playerCollectionURL ||
                window.location.pathname === courseCollectionURL
            );
        },

        registerRouteListener() {
            can.route.ready();
            can.route(':identifier');

            can.route.bind(
                'change',
                (event, attributeName, type, attributeValue) => {
                    if (!(type === 'set' || type === 'add')) {
                        return;
                    }

                    if (
                        this.isPlayerCourseCollectionsPage() &&
                        attributeName === 'identifier'
                    ) {
                        this.viewModel.selectItemByAttribute(
                            'hash',
                            attributeValue,
                        );
                    }
                },
            );
        },

        performRoute(collectionURL, itemIdentifier) {
            if (window.location.pathname === collectionURL) {
                can.route.ready();
                can.route.attr({ identifier: itemIdentifier });
            } else {
                window.location = collectionURL + '#!' + itemIdentifier;
            }
        },

        getCurrentHashItem() {
            const hashValue = urlUtil.getHash().slice(2);
            if (hashValue.startsWith('&')) {
                const attributeObject = can.route.attr();
                const currentItemIdentifier = Object.keys(attributeObject)[0];
                return {
                    identifier: currentItemIdentifier,
                    card: attributeObject[currentItemIdentifier],
                };
            } else if (can.route.attr('identifier')) {
                const attributeArray = can.route.attr('identifier').split('=');
                return {
                    identifier: attributeArray[0],
                    card: attributeArray[1],
                };
            }
            return {
                identifier: hashValue,
                card: null,
            };
        },

        doRoute(collection, itemIdentifier) {
            const encodedIdentifier = encodeItemIdentifier(itemIdentifier);
            if (collection === 'players') {
                this.performRoute(playerCollectionURL, encodedIdentifier);
            } else if (collection === 'courses') {
                this.performRoute(courseCollectionURL, encodedIdentifier);
            }
        },

        '.s-search__list-item click'(element) {
            const elementValue = can.trim(element.data('identifier'));
            const selectedItem = this.viewModel.selectItemByAttribute(
                'identifier',
                elementValue,
            );
            const collection = element.data('collection');
            dtm.trackSearchByPlayerCourse(selectedItem.entry);
            this.doRoute(collection, selectedItem.entry);
        },

        '.js-input click'(element) {
            element.focus();
        },

        '.js-input touchstart'(element) {
            element.focus();
        },

        '.js-submit click'(element) {
            element.closest('.js-search').toggleClass('is-selected');
            if (element.closest('.js-search').hasClass('is-selected')) {
                element
                    .parent()
                    .find('.js-input')
                    .eq(0)
                    .focus()
                    .attr('tabindex', '0');
                element.attr('aria-expanded', true);
            } else {
                element
                    .parent()
                    .find('.js-input')
                    .eq(0)
                    .attr('tabindex', '-1');
                this.viewModel.attr('isActive', false);
                element.attr('aria-expanded', false);
            }
        },

        '.js-mobile-overlay click'(element) {
            element.closest('.js-search').slideUp();
        },

        '.js-input keydown'(element, event) {
            const keyCode = event.keyCode;
            if (keyCode === key.up) {
                this.viewModel.scroll(-1);
                this.viewModel.scrollTableWrapper();
            } else if (keyCode === key.down) {
                this.viewModel.scroll(1);
                this.viewModel.scrollTableWrapper();
            } else if (keyCode === key.esc) {
                this.viewModel.resetSearchValue();
                this.viewModel.attr(
                    'searchValue',
                    this.viewModel.attr('enteredValue'),
                );
                this.viewModel.attr('enteredValue', '');
            } else if (keyCode === key.enter || keyCode === key.tab) {
                const currentItem = this.viewModel
                    .attr('selectedItem')
                    .serialize();
                const collection = currentItem.collection;
                this.doRoute(collection, currentItem.entry);
                this.viewModel.attr('enteredValue', '');
            }
        },

        '{document} click'(element, event) {
            if (!$(event.target).closest('.c-player-course-searchbar').length) {
                this.viewModel.resetSearchValue();
            }
        },
    },
});
